import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Eye, 
  EyeOff, 
  Copy, 
  RefreshCw, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  X,
  Key,
  AlertTriangle,
  CheckCircle,
  Code,
  Shield
} from 'lucide-react';

interface ApiKeySettings {
  length: number;
  format: 'hex' | 'base64' | 'alphanumeric' | 'custom';
  includePrefix: boolean;
  prefix: string;
  includeDashes: boolean;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  customCharset: string;
}

interface ApiKeyAnalytics {
  totalGenerated: number;
  averageLength: number;
  mostCommonFormat: string;
  formatDistribution: {
    hex: number;
    base64: number;
    alphanumeric: number;
    custom: number;
  };
  generationHistory: Array<{
    timestamp: Date;
    length: number;
    format: string;
    hasPrefix: boolean;
  }>;
}

type ApiKeyStrength = 'weak' | 'medium' | 'strong' | 'enterprise';

const ApiKeyGenerator: React.FC = () => {
  const [apiKey, setApiKey] = useState<string>('');
  const [showApiKey, setShowApiKey] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [keyStrength, setKeyStrength] = useState<ApiKeyStrength>('weak');

  const [settings, setSettings] = useState<ApiKeySettings>({
    length: 32,
    format: 'hex',
    includePrefix: true,
    prefix: 'sk_',
    includeDashes: false,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: false,
    customCharset: ''
  });

  const [analytics, setAnalytics] = useState<ApiKeyAnalytics>({
    totalGenerated: 0,
    averageLength: 0,
    mostCommonFormat: 'hex',
    formatDistribution: { hex: 0, base64: 0, alphanumeric: 0, custom: 0 },
    generationHistory: []
  });

  const characterSets = {
    hex: '0123456789abcdef',
    hexUpper: '0123456789ABCDEF',
    base64: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/',
    base64Url: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_',
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?'
  };

  const calculateKeyStrength = useCallback((key: string): ApiKeyStrength => {
    let score = 0;
    const keyWithoutPrefix = settings.includePrefix ? key.replace(settings.prefix, '') : key;
    
    // Length scoring
    if (keyWithoutPrefix.length >= 16) score += 1;
    if (keyWithoutPrefix.length >= 32) score += 1;
    if (keyWithoutPrefix.length >= 64) score += 1;
    
    // Character variety scoring
    if (/[a-z]/.test(keyWithoutPrefix)) score += 1;
    if (/[A-Z]/.test(keyWithoutPrefix)) score += 1;
    if (/[0-9]/.test(keyWithoutPrefix)) score += 1;
    if (/[^A-Za-z0-9]/.test(keyWithoutPrefix)) score += 1;
    
    // Format-specific scoring
    if (settings.format === 'base64' && keyWithoutPrefix.length >= 32) score += 1;
    if (settings.format === 'hex' && keyWithoutPrefix.length >= 32) score += 1;
    
    if (score <= 2) return 'weak';
    if (score <= 4) return 'medium';
    if (score <= 6) return 'strong';
    return 'enterprise';
  }, [settings]);

  const generateCustomKey = useCallback(() => {
    let charset = '';
    
    switch (settings.format) {
      case 'hex':
        charset = characterSets.hex;
        break;
      case 'base64':
        charset = characterSets.base64Url;
        break;
      case 'alphanumeric':
        if (settings.includeUppercase) charset += characterSets.uppercase;
        if (settings.includeLowercase) charset += characterSets.lowercase;
        if (settings.includeNumbers) charset += characterSets.numbers;
        break;
      case 'custom':
        if (settings.customCharset) {
          charset = settings.customCharset;
        } else {
          if (settings.includeUppercase) charset += characterSets.uppercase;
          if (settings.includeLowercase) charset += characterSets.lowercase;
          if (settings.includeNumbers) charset += characterSets.numbers;
          if (settings.includeSymbols) charset += characterSets.symbols;
        }
        break;
    }
    
    if (charset === '') {
      charset = characterSets.hex; // Fallback
    }
    
    let keyBody = '';
    for (let i = 0; i < settings.length; i++) {
      keyBody += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    // Add dashes if enabled
    if (settings.includeDashes && settings.format !== 'base64') {
      const dashInterval = Math.max(4, Math.floor(keyBody.length / 4));
      keyBody = keyBody.match(new RegExp(`.{1,${dashInterval}}`, 'g'))?.join('-') || keyBody;
    }
    
    // Add prefix if enabled
    const finalKey = settings.includePrefix ? settings.prefix + keyBody : keyBody;
    
    return finalKey;
  }, [settings]);

  const generateApiKey = useCallback(() => {
    const newKey = generateCustomKey();
    setApiKey(newKey);
    
    const strength = calculateKeyStrength(newKey);
    setKeyStrength(strength);
    
    // Update analytics
    setAnalytics(prev => {
      const newHistory = [...prev.generationHistory, {
        timestamp: new Date(),
        length: newKey.length,
        format: settings.format,
        hasPrefix: settings.includePrefix
      }].slice(-100);
      
      const totalGenerated = prev.totalGenerated + 1;
      const avgLength = (prev.averageLength * prev.totalGenerated + newKey.length) / totalGenerated;
      
      const formatDist = { ...prev.formatDistribution };
      formatDist[settings.format]++;
      
      return {
        totalGenerated,
        averageLength: Math.round(avgLength * 10) / 10,
        mostCommonFormat: settings.format,
        formatDistribution: formatDist,
        generationHistory: newHistory
      };
    });
  }, [settings, calculateKeyStrength, generateCustomKey]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy API key');
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getStrengthColor = (strength: ApiKeyStrength): string => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-blue-500';
      case 'enterprise': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStrengthText = (strength: ApiKeyStrength): string => {
    switch (strength) {
      case 'weak': return 'Weak';
      case 'medium': return 'Medium';
      case 'strong': return 'Strong';
      case 'enterprise': return 'Enterprise';
      default: return 'Unknown';
    }
  };

  const getFormatDescription = (format: string): string => {
    switch (format) {
      case 'hex': return 'Hexadecimal (0-9, a-f)';
      case 'base64': return 'Base64 URL-safe (A-Z, a-z, 0-9, -, _)';
      case 'alphanumeric': return 'Alphanumeric characters only';
      case 'custom': return 'Custom character set';
      default: return '';
    }
  };

  useEffect(() => {
    generateApiKey();
  }, []);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-purple-50 to-blue-100'}
    transition-all duration-300
  `;

  return (
    <>
      <Helmet>
        <title>API Key Generator - Secure, Random API Keys for Development</title>
        <meta name="description" content="Generate secure, random API keys with customizable formats including hex, base64, and alphanumeric. Perfect for developers, APIs, and secure authentication systems." />
        <meta name="keywords" content="API key generator, random API key, secure key generation, hex key, base64 key, developer tools, authentication, security" />
        <meta name="author" content="API Key Generator Tool" />
        <meta property="og:title" content="API Key Generator - Secure Random Keys" />
        <meta property="og:description" content="Generate secure API keys with multiple formats. Hex, Base64, Alphanumeric options with customizable length and prefixes." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="API Key Generator - Secure Random Keys" />
        <meta name="twitter:description" content="Professional API key generation tool with multiple formats and security options." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "API Key Generator",
            "description": "Generate secure, random API keys with customizable formats",
            "url": window.location.href,
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>

      <div className={containerClasses}>
        <div className={`${isDarkMode ? 'dark' : ''}`}>
          <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
            {/* Header */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
                <div className="flex items-center space-x-3">
                  <Key className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                      API Key Generator
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Generate secure API keys for your applications
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowAnalytics(!showAnalytics)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title="Analytics"
                  >
                    <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title="Settings"
                  >
                    <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  
                  <button
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title="Toggle Theme"
                  >
                    {isDarkMode ? (
                      <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>
                  
                  <button
                    onClick={toggleFullscreen}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  >
                    {isFullscreen ? (
                      <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main API Key Generator */}
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <div className="space-y-6">
                    {/* API Key Display */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Generated API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKey ? "text" : "password"}
                          value={apiKey}
                          readOnly
                          className="w-full p-4 pr-24 text-lg font-mono bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white break-all"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                          <button
                            onClick={() => setShowApiKey(!showApiKey)}
                            className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title={showApiKey ? "Hide" : "Show"}
                          >
                            {showApiKey ? (
                              <EyeOff className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            ) : (
                              <Eye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={copyToClipboard}
                            className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title="Copy"
                          >
                            {copied ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Key Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Format</div>
                        <div className="font-medium text-gray-800 dark:text-white capitalize">
                          {settings.format}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {getFormatDescription(settings.format)}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Length</div>
                        <div className="font-medium text-gray-800 dark:text-white">
                          {apiKey.length} characters
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {settings.includePrefix ? `${settings.prefix.length} prefix + ${apiKey.length - settings.prefix.length} key` : 'Full key length'}
                        </div>
                      </div>
                    </div>

                    {/* API Key Strength */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Security Level: {getStrengthText(keyStrength)}
                        </span>
                        <div className="flex items-center space-x-2">
                          <Shield className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Cryptographically secure
                          </span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(keyStrength)}`}
                          style={{
                            width: keyStrength === 'weak' ? '25%' :
                                   keyStrength === 'medium' ? '50%' :
                                   keyStrength === 'strong' ? '75%' : '100%'
                          }}
                        />
                      </div>
                    </div>

                    {/* Generate Button */}
                    <button
                      onClick={generateApiKey}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                    >
                      <RefreshCw className="w-5 h-5" />
                      <span>Generate New API Key</span>
                    </button>

                    {/* Security Notice */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800 dark:text-blue-200">
                          <strong>Security Notice:</strong> Store your API keys securely and never expose them in client-side code or public repositories. Consider using environment variables and key rotation practices.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Panel */}
              {showSettings && (
                <div className="lg:col-span-1">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center space-x-2">
                      <Code className="w-5 h-5" />
                      <span>Configuration</span>
                    </h3>
                    
                    <div className="space-y-4">
                      {/* Length */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Key Length: {settings.length}
                        </label>
                        <input
                          type="range"
                          min="8"
                          max="128"
                          value={settings.length}
                          onChange={(e) => setSettings(prev => ({ ...prev, length: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        />
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>8</span>
                          <span>128</span>
                        </div>
                      </div>

                      {/* Format */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Format
                        </label>
                        <select
                          value={settings.format}
                          onChange={(e) => setSettings(prev => ({ ...prev, format: e.target.value as any }))}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                        >
                          <option value="hex">Hexadecimal</option>
                          <option value="base64">Base64 URL-safe</option>
                          <option value="alphanumeric">Alphanumeric</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>

                      {/* Prefix */}
                      <div>
                        <label className="flex items-center space-x-3 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={settings.includePrefix}
                            onChange={(e) => setSettings(prev => ({ ...prev, includePrefix: e.target.checked }))}
                            className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Include Prefix</span>
                        </label>
                        {settings.includePrefix && (
                          <input
                            type="text"
                            value={settings.prefix}
                            onChange={(e) => setSettings(prev => ({ ...prev, prefix: e.target.value }))}
                            placeholder="sk_"
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                          />
                        )}
                      </div>

                      {/* Additional Options */}
                      {(settings.format === 'alphanumeric' || settings.format === 'custom') && (
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Character Options</h4>
                          {[
                            { key: 'includeUppercase', label: 'Uppercase (A-Z)' },
                            { key: 'includeLowercase', label: 'Lowercase (a-z)' },
                            { key: 'includeNumbers', label: 'Numbers (0-9)' },
                            { key: 'includeSymbols', label: 'Symbols (!@#$...)' }
                          ].map(({ key, label }) => (
                            <label key={key} className="flex items-center space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={settings[key as keyof ApiKeySettings] as boolean}
                                onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                                className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {/* Custom Charset */}
                      {settings.format === 'custom' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Custom Character Set
                          </label>
                          <textarea
                            value={settings.customCharset}
                            onChange={(e) => setSettings(prev => ({ ...prev, customCharset: e.target.value }))}
                            placeholder="Enter custom characters..."
                            rows={3}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                          />
                        </div>
                      )}

                      {/* Dashes */}
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.includeDashes}
                          onChange={(e) => setSettings(prev => ({ ...prev, includeDashes: e.target.checked }))}
                          className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Include Dashes</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Analytics Panel */}
              {showAnalytics && (
                <div className="lg:col-span-1">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center space-x-2">
                      <BarChart3 className="w-5 h-5" />
                      <span>Analytics</span>
                    </h3>
                    
                    <div className="space-y-4">
                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {analytics.totalGenerated}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            Keys Generated
                          </div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {analytics.averageLength}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            Avg Length
                          </div>
                        </div>
                      </div>

                      {/* Format Distribution */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Format Usage
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(analytics.formatDistribution).map(([format, count]) => (
                            <div key={format} className="flex justify-between items-center text-sm">
                              <span className="text-gray-600 dark:text-gray-400 capitalize">
                                {format}
                              </span>
                              <span className="font-medium text-gray-800 dark:text-white">
                                {count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Recent History */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Recent Keys
                        </h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {analytics.generationHistory.slice(-5).reverse().map((entry, index) => (
                            <div key={index} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                              <span>{entry.length} chars</span>
                              <span className="capitalize">{entry.format}</span>
                              {entry.hasPrefix && <span className="text-purple-500">+prefix</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApiKeyGenerator;